import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { errMessage } from "@/components/LoadError";
import { Truck, Lock, User } from "lucide-react";

const BG_IMG =
  "https://images.pexels.com/photos/9716225/pexels-photo-9716225.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(() => {
    try {
      const reason = localStorage.getItem("tw1_login_reason");
      if (reason) { localStorage.removeItem("tw1_login_reason"); return reason; }
    } catch { /* ignore */ }
    return "";
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username.trim().toLowerCase(), password);
    } catch (err) {
      // Geen response = netwerk/CORS (bv. verouderde link): toon de echte oorzaak i.p.v. "wachtwoord fout".
      setError(err?.response ? (err.response.data?.detail || "Inloggen mislukt") : errMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4 relative overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${BG_IMG})` }}
      />
      <div className="absolute inset-0 bg-zinc-950/80" />

      <div className="w-full max-w-md relative z-10">
        <div className="flex items-center gap-3 mb-8 text-white">
          <div className="h-12 w-12 rounded-md bg-blue-600 flex items-center justify-center ring-1 ring-white/10">
            <Truck className="h-6 w-6" strokeWidth={2.2} />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-black tracking-tight">OM-Cockpit · TW1</h1>
            <p className="text-zinc-400 text-xs uppercase tracking-widest font-semibold">Operationeel Command-Center</p>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="login-form"
          className="bg-white rounded-[12px] border border-zinc-300 p-7 shadow-2xl"
        >
          <h2 className="font-heading text-xl font-bold text-zinc-900 mb-1">Inloggen</h2>
          <p className="text-sm text-zinc-500 mb-6">Log in met je TW1-account.</p>

          <label className="block text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-1.5">Gebruikersnaam</label>
          <div className="relative mb-4">
            <User className="h-4 w-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="login-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              className="w-full pl-9 pr-3 py-2.5 rounded-md border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 text-zinc-900 transition-shadow"
              placeholder="jouw gebruikersnaam"
            />
          </div>

          <label className="block text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-1.5">Wachtwoord</label>
          <div className="relative mb-5">
            <Lock className="h-4 w-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full pl-9 pr-3 py-2.5 rounded-md border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 text-zinc-900 transition-shadow"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div data-testid="login-error" className="mb-4 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700 font-medium">
              {error}
            </div>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full py-2.5 rounded-md bg-zinc-950 hover:bg-zinc-800 text-white font-semibold tracking-wide transition-colors disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
          >
            {loading ? "Bezig…" : "Inloggen"}
          </button>
        </form>
      </div>
    </div>
  );
}
